import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import en from './en';
import ko from './ko';

// Translation for the whole site.
//
// Hand-rolled rather than react-i18next: two locales, ~190 strings, and an app
// with no state library and no CSS framework. The library would be more code
// than the thing it translates. The catalogue shape is conventional enough that
// swapping to one later is a mechanical change, not a rewrite.
//
// Scope was once the signed-in pages only; it is now everything. The toggle
// lives in the header, so it is reachable from the landing page, and the choice
// applies wherever the reader goes.
//
// What stays English is decided per string, not per page: an artwork's TITLE
// and its ARTIST are the work's identity, not copy, and translating them is
// simply wrong — "The Last Shawarma" is the name of the piece. Those come from
// Constants.js and the artwork components and are never routed through `t`.
// Only the chrome around them is.
//
// Korean has no plural forms, which removes the usual reason to reach for a
// library. Counts are handled by giving English `_one`/`_other` variants and
// letting Korean supply a single string.

const LOCALES = { en, ko };

export const LANGUAGES = [
    ['en', 'EN'],
    ['ko', '한국어'],
];

/** Persisted per browser, not per user in the database: notes and nicknames are
 *  shared because the portfolio is shared, but a reading language is personal
 *  and should not follow one person's choice onto someone else's screen. */
const STORAGE_KEY = 'kanosei.lang';

const readStored = () => {
    try {
        const v = window.localStorage.getItem(STORAGE_KEY);
        return v && LOCALES[v] ? v : 'en';
    } catch {
        // Safari in private mode throws on localStorage access rather than
        // returning null. English is the right thing to fall back to.
        return 'en';
    }
};

/**
 * Parses a value for display.
 *
 * A bare `YYYY-MM-DD` is parsed as a LOCAL date, not through `new Date(iso)`,
 * which reads it as UTC midnight. West of UTC that lands on the previous day:
 * the index's inception of 2026-08-01 rendered as 31 July for every reader in
 * the Americas, and each account's as-of date was a day behind the statement it
 * came from. Timestamps carry their own zone and are left alone.
 */
const parseForDisplay = (value) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
};

const LanguageContext = createContext(null);

/** `{name}` placeholders, replaced positionally from `vars`. */
const interpolate = (template, vars) =>
    vars
        ? template.replace(/\{(\w+)\}/g, (whole, key) =>
            Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : whole)
        : template;

const lookup = (table, key) =>
    key.split('.').reduce((acc, part) => (acc == null ? acc : acc[part]), table);

export function LanguageProvider({ children }) {
    const [lang, setLang] = useState(readStored);

    // No route gate any more. The choice applies everywhere, which is the point
    // of moving the toggle into the header: a reader who switches on the
    // landing page stays switched when they open the collection.
    const active = lang;

    const change = useCallback((next) => {
        if (!LOCALES[next]) return;
        setLang(next);
        try {
            window.localStorage.setItem(STORAGE_KEY, next);
        } catch {
            // Non-fatal: the choice applies for this session and is simply not
            // remembered. Better than refusing to switch.
        }
    }, []);

    const value = useMemo(() => {
        const table = LOCALES[active];

        /**
         * Looks a key up, interpolating `{placeholders}`.
         *
         * A missing key returns the ENGLISH string rather than the key itself.
         * A half-translated page is readable; a page of `rules.cadence.title`
         * is not, and the fallback means Korean can be filled in incrementally
         * without shipping visible breakage.
         */
        const t = (key, vars) => {
            const hit = lookup(table, key) ?? lookup(en, key);
            if (typeof hit !== 'string') return key;
            return interpolate(hit, vars);
        };

        /** Count-aware lookup. English gets `_one`/`_other`; Korean, which has
         *  no plural forms, supplies the bare key and ignores the distinction. */
        const tn = (key, count, vars) => {
            const suffixed = count === 1 ? `${key}_one` : `${key}_other`;
            const hit = lookup(table, suffixed) ?? lookup(table, key)
                ?? lookup(en, suffixed) ?? lookup(en, key);
            if (typeof hit !== 'string') return key;
            return interpolate(hit, { count, ...vars });
        };

        /**
         * Translates a VALUE that arrived as data rather than a UI string —
         * a sector name, an asset type.
         *
         * Falls back to the raw value, never to a key or a blank. FMP can start
         * returning a category nobody has mapped yet, and showing it in English
         * is right; showing nothing would quietly empty a column.
         */
        const td = (group, value) => {
            if (!value) return value;
            const hit = table?.data?.[group]?.[value] ?? en.data?.[group]?.[value];
            return hit ?? value;
        };

        // Intl locale, not the app's language code — they happen to differ.
        const intl = active === 'ko' ? 'ko-KR' : 'en-US';

        const fmt = {
            /** Decimal strings arrive correct from the API and only need
             *  grouping; never re-derived through float arithmetic here. */
            number: (v, decimals = 2) => {
                if (v === null || v === undefined || v === '') return null;
                const n = Number(v);
                if (!Number.isFinite(n)) return String(v);
                return n.toLocaleString(intl, {
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals,
                });
            },
            /** ISO date or timestamp -> a date in the reader's convention.
             *  `2026. 8. 26.` in Korean, `Aug 26, 2026` in English. */
            date: (v) => {
                if (!v) return null;
                const d = parseForDisplay(v);
                if (Number.isNaN(d.getTime())) return String(v);
                return d.toLocaleDateString(intl, {
                    year: 'numeric', month: 'short', day: 'numeric',
                });
            },
            dateTime: (v) => {
                if (!v) return null;
                const d = new Date(v);
                if (Number.isNaN(d.getTime())) return String(v);
                return d.toLocaleString(intl);
            },
        };

        // `active` is kept as a distinct field even though it now always
        // equals `lang`: callers read it, and collapsing the two would be a
        // wider change than this one.
        return { lang, active, setLang: change, t, tn, td, fmt, locale: intl };
    }, [lang, active, change]);

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/**
 * Translation for a component.
 *
 * Safe outside the provider — the public pages are not wrapped, and a shared
 * component like Header renders on both sides of that boundary. Falling back to
 * English there is exactly the intended behaviour, not a degraded mode.
 */
export function useT() {
    const ctx = useContext(LanguageContext);
    if (ctx) return ctx;
    return {
        lang: 'en',
        setLang: () => {},
        t: (key, vars) => {
            const hit = lookup(en, key);
            return typeof hit === 'string' ? interpolate(hit, vars) : key;
        },
        tn: (key, count, vars) => {
            const hit = lookup(en, count === 1 ? `${key}_one` : `${key}_other`) ?? lookup(en, key);
            return typeof hit === 'string' ? interpolate(hit, { count, ...vars }) : key;
        },
        td: (group, value) => (value ? (en.data?.[group]?.[value] ?? value) : value),
        fmt: {
            number: (v, d = 2) => {
                if (v === null || v === undefined || v === '') return null;
                const n = Number(v);
                return Number.isFinite(n)
                    ? n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })
                    : String(v);
            },
            date: (v) => (v ? parseForDisplay(v).toLocaleDateString('en-US', {
                year: 'numeric', month: 'short', day: 'numeric',
            }) : null),
            dateTime: (v) => (v ? new Date(v).toLocaleString('en-US') : null),
        },
        locale: 'en-US',
    };
}

/**
 * `useT` for a class component, injected as a `t` prop.
 *
 * The public pages are class components with real lifecycle work — Collection
 * constructs and destroys the Swiper in componentDidMount/WillUnmount, and the
 * carousel is fragile enough that the CLAUDE.md notes warn against touching it.
 * Converting them to hooks to reach the catalogue would be a rewrite of the
 * risky part in order to change the safe part, so the translation comes to them
 * instead.
 */
export function withT(Component) {
    const Wrapped = (props) => {
        const { t, lang } = useT();
        // `lang` is passed so a class component re-renders on a language
        // change: it is not otherwise reading anything that moved.
        return <Component {...props} t={t} lang={lang} />;
    };
    Wrapped.displayName = `withT(${Component.displayName || Component.name || 'Component'})`;
    return Wrapped;
}

/**
 * Renders a catalogue string, turning a `*marked run*` into <em>.
 *
 * The alternative — before/em/after key triples — puts the sentence's shape in
 * the component, where a translator cannot move the emphasis. Korean does not
 * stress the same clause English does, so the marker travels with the string.
 */
export function Rich({ text }) {
    const parts = String(text).split(/\*([^*]+)\*/g);
    return (
        <>
            {parts.map((part, i) =>
                // Odd indices are the captured groups — the emphasised runs.
                i % 2 ? <em key={i}>{part}</em> : part)}
        </>
    );
}
